/**
 * Bulk Events Component
 * 
 * Main component for bulk event creation feature.
 * Manages state, data fetching, and orchestrates child components.
 * 
 * Requirements: 1.1-1.7, 2.1-2.4, 3.1-3.12, 5.1-5.5, 6.1-6.10, 7.1-7.5, 8.1-8.6, 10.1-10.5
 */

import { useState, useEffect } from 'react';
import { restClient, EventType, Location } from '../../../api/rest-client';
import { BulkEventsForm } from './BulkEventsForm';
import { EventPreview } from './EventPreview';
import { BatchCreationProgress, BatchProgress } from './BatchCreationProgress';
import { ResultsSummary, BatchResults } from './ResultsSummary';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { Card } from '../../common/Card';
import { getFirstMondayOfNovember, formatDateForInput } from '../../../utils/dateUtils';
import { validateBulkEventsConfig, BulkEventsConfig, DayConfig } from '../../../utils/bulkEventsValidation';
import { generateEventsList, calculateEventCount } from '../../../utils/bulkEventsGenerator';
import { createEventsSequentially } from '../../../utils/bulkEventsCreation';

/**
 * Bulk Events Component
 * Requirements: 1.1-1.7, 2.1-2.4, 3.1-3.12, 5.1-5.5, 6.1-6.10, 7.1-7.5, 8.1-8.6, 10.1-10.5
 */
export function BulkEvents() {
  // Data state
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [season, setSeason] = useState<string>('');
  
  // Form state
  const [config, setConfig] = useState<BulkEventsConfig>(() => {
    const currentYear = new Date().getFullYear();
    // Default start: July 1st of current year
    const defaultStart = new Date(currentYear, 6, 1); // Month is 0-indexed, so 6 = July
    const defaultEnd = getFirstMondayOfNovember(currentYear);
    
    return {
      startDate: formatDateForInput(defaultStart),
      endDate: formatDateForInput(defaultEnd),
      recurrenceWeeks: 1,
      weeklySchedule: Array.from({ length: 7 }, (_, i): DayConfig => ({
        dayOfWeek: i,
        enabled: false,
        eventName: '',
        eventType: '',
        locationId: '',
        startTime: '',
        endTime: '',
      })),
    };
  });
  
  const [errors, setErrors] = useState<Record<string, any>>({});
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewEvents, setPreviewEvents] = useState<any[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState<BatchProgress>({ current: 0, total: 0 });
  const [results, setResults] = useState<BatchResults | null>(null);
  const [showResults, setShowResults] = useState(false);
  
  // Load initial data
  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setLoadError(null);
        
        const [typesData, locationsData, seasonData] = await Promise.all([
          restClient.getEventTypes(),
          restClient.getLocations(),
          restClient.getSeasonConfig(),
        ]);
        
        setEventTypes(typesData);
        setLocations(locationsData);
        setSeason(seasonData.currentSeason || new Date().getFullYear().toString());
      } catch (error) {
        console.error('[BulkEvents] Failed to load data:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    }
    
    loadData();
  }, []);
  
  // Handle configuration changes
  const handleConfigChange = (newConfig: BulkEventsConfig) => {
    setConfig(newConfig);
    
    // Clear errors when config changes
    setErrors({});
  };
  
  // Handle preview
  const handlePreview = () => {
    // Validate configuration
    const validation = validateBulkEventsConfig(config);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    // Generate preview events
    const events = generateEventsList(config, season);
    setPreviewEvents(events);
    setShowPreview(true);
  };
  
  // Handle create (first confirmation from preview)
  const handleCreateFromPreview = () => {
    setShowPreview(false);
    
    // Show second confirmation dialog
    const count = calculateEventCount(config);
    const confirmed = window.confirm(
      `Are you sure you want to create ${count} events?\n\n` +
      `This action cannot be undone. Events will be created one at a time with a 1-second delay between each.`
    );
    
    if (confirmed) {
      handleCreateConfirmed();
    }
  };
  
  // Handle create (direct from form)
  const handleCreate = () => {
    // Validate configuration
    const validation = validateBulkEventsConfig(config);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    // Generate events for count
    const count = calculateEventCount(config);
    
    // Show first confirmation dialog
    const confirmed = window.confirm(
      `You are about to create ${count} events.\n\n` +
      `Do you want to preview the events first?`
    );
    
    if (confirmed) {
      handlePreview();
    } else {
      // Show second confirmation
      const doubleConfirmed = window.confirm(
        `Are you sure you want to create ${count} events without previewing?\n\n` +
        `This action cannot be undone. Events will be created one at a time with a 1-second delay between each.`
      );
      
      if (doubleConfirmed) {
        handleCreateConfirmed();
      }
    }
  };
  
  // Actually create the events
  const handleCreateConfirmed = async () => {
    try {
      setIsCreating(true);
      setProgress({ current: 0, total: 0 });
      
      // Generate events
      const events = generateEventsList(config, season);
      
      // Create events sequentially
      const batchResults = await createEventsSequentially(events, setProgress);
      
      // Show results
      setResults(batchResults);
      setShowResults(true);
    } catch (error) {
      console.error('[BulkEvents] Failed to create events:', error);
      alert(`Failed to create events: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCreating(false);
    }
  };
  
  // Handle results close
  const handleResultsClose = () => {
    setShowResults(false);
    
    // Reset form after closing results (regardless of success/failure)
    const currentYear = new Date().getFullYear();
    const defaultStart = new Date(currentYear, 6, 1); // July 1st
    const defaultEnd = getFirstMondayOfNovember(currentYear);
    
    setConfig({
      startDate: formatDateForInput(defaultStart),
      endDate: formatDateForInput(defaultEnd),
      recurrenceWeeks: 1,
      weeklySchedule: Array.from({ length: 7 }, (_, i): DayConfig => ({
        dayOfWeek: i,
        enabled: false,
        eventName: '',
        eventType: '',
        locationId: '',
        startTime: '',
        endTime: '',
      })),
    });
    
    setResults(null);
    setErrors({});
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  
  // Error state
  if (loadError) {
    return (
      <Card className="bg-red-50 border border-red-200">
        <h3 className="text-lg font-semibold text-red-600 mb-2">
          Failed to Load
        </h3>
        <p className="text-red-700">{loadError}</p>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Bulk Events</h2>
        <p className="mt-1 text-sm text-gray-600">
          Create multiple recurring events at once. Select a date range, recurrence pattern,
          and configure which days of the week to create events.
        </p>
      </div>
      
      {/* Progress Indicator */}
      <BatchCreationProgress
        progress={progress}
        isVisible={isCreating}
      />
      
      {/* Form */}
      <BulkEventsForm
        config={config}
        eventTypes={eventTypes}
        locations={locations}
        errors={errors}
        isLoading={isCreating}
        onConfigChange={handleConfigChange}
        onPreview={handlePreview}
        onCreate={handleCreate}
      />
      
      {/* Preview Modal */}
      <EventPreview
        events={previewEvents}
        locations={locations}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onConfirm={handleCreateFromPreview}
      />
      
      {/* Results Modal */}
      <ResultsSummary
        results={results}
        isOpen={showResults}
        onClose={handleResultsClose}
      />
    </div>
  );
}

export default BulkEvents;
